import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/lib/safeDespia";
import {
  fetchOneSignalPlayerId,
  requestNotificationPermission,
  scheduleDailyPush,
  openAppSettings,
  setOneSignalExternalUserId,
  waitForOneSignalPlayerId,
} from "@/lib/despiaNotifications";
import { supabase } from "@/integrations/supabase/client";

// ── local-storage keys ─────────────────────────────────────────────────
const PERMISSION_KEY = "spotrai_push_permission";
const PROMPTED_KEY = "spotrai_push_permission_prompted_once";

type PermissionState = "prompt" | "granted" | "denied";

// ── safe storage helpers ───────────────────────────────────────────────
const safeGet = (key: string) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* no-op */ }
};

const readCached = (): PermissionState => {
  const v = safeGet(PERMISSION_KEY);
  return v === "granted" || v === "denied" ? v : "prompt";
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Singleton guard so we never run two permission requests in parallel
let _permPromise: Promise<boolean> | null = null;

export function usePushNotifications() {
  const { user } = useAuth();
  const [permissionState, setPermState] = useState<PermissionState>(readCached);

  // ── save player ID to database ────────────────────────────────────
  const savePlayerId = useCallback(async (userId: string) => {
    const playerId = await fetchOneSignalPlayerId();
    if (!playerId) {
      console.log("[push] No player ID to save");
      return;
    }

    console.log("[push] Saving player ID to DB:", playerId, "for user:", userId);
    try {
      await supabase
        .from("push_subscriptions" as any)
        .upsert(
          { user_id: userId, player_id: playerId, platform: "ios", updated_at: new Date().toISOString() },
          { onConflict: "user_id,player_id" },
        );
    } catch (e) {
      console.warn("[push] Failed to save player ID", e);
    }
  }, []);

  // ── sync permission state by checking for a valid OneSignal player ID ──
  const syncPermission = useCallback(async (): Promise<PermissionState> => {
    if (!isNativeApp()) return "prompt";

    const playerId = await fetchOneSignalPlayerId();
    console.log("[push] syncPermission — playerId:", playerId);

    if (playerId) {
      safeSet(PERMISSION_KEY, "granted");
      setPermState("granted");
      if (user?.id) {
        void savePlayerId(user.id);
      }
      return "granted";
    }

    // No player ID — either not prompted or denied.
    // We can't distinguish prompt vs denied from the SDK alone,
    // so check if we've prompted before.
    const prompted = safeGet(PROMPTED_KEY) === "true";
    const state: PermissionState = prompted ? "denied" : "prompt";
    safeSet(PERMISSION_KEY, state);
    setPermState(state);
    return state;
  }, [savePlayerId, user?.id]);

  // ── request permission (idempotent, singleton) ────────────────────
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isNativeApp()) return false;
    if (_permPromise) return _permPromise;

    _permPromise = (async () => {
      safeSet(PROMPTED_KEY, "true");
      await requestNotificationPermission();

      // Give iOS time to register with OneSignal after permission grant
      await sleep(2000);

      const status = await syncPermission();
      return status === "granted";
    })().finally(() => { _permPromise = null; });

    return _permPromise;
  }, [syncPermission]);

  // ── first-launch auto-prompt ──────────────────────────────────────
  const requestPermissionOnFirstLaunch = useCallback(async () => {
    if (!isNativeApp()) return;
    const prompted = safeGet(PROMPTED_KEY) === "true";
    if (prompted) {
      await syncPermission();
      return;
    }
    await requestPermission();
  }, [requestPermission, syncPermission]);

  // ── schedule a daily reminder ─────────────────────────────────────
  const scheduleDailyReminder = useCallback(
    async (
      hour: number,
      minute: number,
      identifier: string,
      title: string,
      body: string,
    ): Promise<boolean> => {
      return scheduleDailyPush(hour, minute, identifier, title, body);
    },
    [],
  );

  // ── cancel (no-op in Despia, but we keep the interface) ───────────
  const cancelNotification = useCallback(async (_identifier: string): Promise<boolean> => {
    return true;
  }, []);

  const markDenied = useCallback(() => {
    safeSet(PROMPTED_KEY, "true");
    safeSet(PERMISSION_KEY, "denied");
    setPermState("denied");
  }, []);

  // ── effects ───────────────────────────────────────────────────────

  // Sync permission on mount
  useEffect(() => {
    if (!isNativeApp()) return;
    void syncPermission();
  }, [syncPermission]);

  // Link OneSignal identity for logged-in native users
  useEffect(() => {
    if (!isNativeApp() || !user?.id) return;
    console.log("[push] Syncing OneSignal external user ID on login:", user.id);
    void setOneSignalExternalUserId(user.id);
  }, [user?.id]);

  // Save player ID when permission is granted and user is logged in
  useEffect(() => {
    if (!isNativeApp() || permissionState !== "granted" || !user?.id) return;
    void savePlayerId(user.id);
  }, [permissionState, user?.id, savePlayerId]);

  return {
    isSupported: isNativeApp(),
    isEnabled: permissionState === "granted",
    permissionState,
    requestPermission,
    requestPermissionOnFirstLaunch,
    syncPermissionState: syncPermission,
    markDenied,
    openSettings: openAppSettings,
    scheduleDailyReminder,
    cancelNotification,
  };
}
